import api from "../../services/api-configuration";

// Digital chain of custody / RUA handover inspection (backend: /api/v1/custody/*)

export const getMasterData = async () => (await api.post("/custody/masterData", {})).data;

export const getCustodyVehicle = async (chassisNo) =>
    (await api.post("/custody/vehicle", { chassis_no: chassisNo })).data;

export const getHandoversByChassis = async (chassisNo) =>
    (await api.post("/custody/byChassis", { chassis_no: chassisNo })).data;

export const getHandover = async (id) => (await api.post("/custody/handover", { id })).data;

export const verifyChain = async (chassisNo) =>
    (await api.post("/custody/verifyChain", { chassis_no: chassisNo })).data;

// lines[i].photos = File[]; sent as photo_<lineIndex>_<n>; pod = photos / PDF of the signed delivery note, sent as pod_<n>
export const createHandover = async (data, lines, pod = []) => {
    const form = new FormData();
    const body = { ...data, lines: lines.map(({ photos, ...l }) => l) };
    form.append("data", JSON.stringify(body));
    lines.forEach((l, i) => (l.photos || []).forEach((f, n) => form.append(`photo_${i}_${n + 1}`, f, f.name)));
    pod.forEach((f, n) => form.append(`pod_${n + 1}`, f, f.name));
    return (await api.post("/custody/createHandover", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

const fileCache = {};
// Photos and signatures need the auth token, so they are fetched as blobs, not via <img src>.
export const getCustodyFileUrl = async (path) => {
    if (!fileCache[path]) {
        const res = await api.get("/custody/file", { params: { path }, responseType: "blob" });
        fileCache[path] = URL.createObjectURL(res.data);
    }
    return fileCache[path];
};

export const getCustodyFileDataUrl = async (path) => {
    const res = await api.get("/custody/file", { params: { path }, responseType: "blob" });
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(res.data);
    });
};

// ---- phase B
export const getMyCustodyPermissions = async () => (await api.post("/custody/myPermissions", {})).data;
export const startHandover = async (data) => (await api.post("/custody/startHandover", data)).data;
export const getOpenStarts = async () => (await api.post("/custody/openStarts", {})).data;
export const getOpenDamages = async () => (await api.post("/custody/openDamages", {})).data;
export const updateDamageStatus = async (body) => (await api.post("/custody/updateDamageStatus", body)).data;
export const getCustodyIntervals = async (chassisNo) => (await api.post("/custody/intervals", { chassis_no: chassisNo })).data;
export const getCustodyReport = async (from, to) => (await api.post("/custody/report", { from, to })).data;
export const saveCustodyCode = async (table, code, values) => (await api.post("/custody/saveCode", { table, code, values })).data;

// ---- phase C: configurable flow
// the operator's site (warehouse chosen at login) tells where a car that is not yet born is being born
const siteWarehouseId = () => {
    try { return parseInt(JSON.parse(atob(localStorage.getItem('warehouse') || '')), 10) || 0; } catch (e) { return 0; }
};
export const getHandoverContext = async (body) => (await api.post("/custody/handoverContext", { site_warehouse_id: siteWarehouseId(), ...body })).data;
export const getGateEvents = async (onlyExceptions) => (await api.post("/custody/gateEvents", { only_exceptions: onlyExceptions })).data;
export const saveFlowConfig = async (table, key, values) => (await api.post("/custody/saveFlowConfig", { table, key, values })).data;

// final deliveries (proof of delivery) between two dates (YYYY-MM-DD)
export const getDeliveries = async (from, to) => (await api.post("/custody/deliveries", { from, to })).data;

// delivery not accepted at the dealer: reason, signatures, photos (photo_<n>); opens the return case
export const refuseDelivery = async (data, photos = []) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    photos.forEach((f, n) => form.append(`photo_${n + 1}`, f, f.name));
    return (await api.post("/custody/refuseDelivery", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

export const getReturnCases = async (onlyOpen, chassisNo = '') =>
    (await api.post("/custody/returnCases", { only_open: onlyOpen, chassis_no: chassisNo })).data;

// active general blockages of a VIN by type (null when none)
export const getVehicleBlocking = async (chassisNo) => (await api.post("/custody/vehicleBlocking", { chassis_no: chassisNo })).data;

// every order of a VIN from the order table (also orders without an operation-log entry)
export const getVehicleOrders = async (chassisNo) => (await api.post("/custody/vehicleOrders", { chassis_no: chassisNo })).data;

export const updateReturnCase = async (body) => (await api.post("/custody/updateReturnCase", body)).data;

// SLA / exception list (backend: /api/v1/custody/sla, saveSlaRule; roles sla_read / sla_write)
export const getSla = async () => (await api.post("/custody/sla", {})).data;
export const saveSlaRule = async (code, values) => (await api.post("/custody/saveSlaRule", { code, values })).data;

// Quality control by stock age (backend: /api/v1/custody/qualityControl, recordQualityCheck, saveQcRule)
export const getQualityControl = async () => (await api.post("/custody/qualityControl", {})).data;
export const recordQualityCheck = async (body) => (await api.post("/custody/recordQualityCheck", body)).data;
export const saveQcRule = async (code, values) => (await api.post("/custody/saveQcRule", { code, values })).data;

// Load planning (backend: /api/v1/custody/transportProfiles, loadPlan)
export const getTransportProfiles = async () => (await api.post("/custody/transportProfiles", {})).data;
export const getLoadPlan = async (body) => (await api.post("/custody/loadPlan", body)).data;

// Sites, zones and operators (backend: /api/v1/custody/sites, saveSite)
export const getSites = async () => (await api.post("/custody/sites", {})).data;
export const saveSite = async (body) => (await api.post("/custody/saveSite", body)).data;
// operator documents for a step of the chain (RUA report, handover PDF): data (JSON) + document
export const attachDocument = async (data, file) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    form.append("document", file);
    return (await api.post("/custody/attachDocument", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

// Companies (Tanımlamalar > Firmalar; backend: /api/v1/custody/companies, saveCompany)
export const getCompanies = async () => (await api.post("/custody/companies", {})).data;
export const saveCompany = async (key, values) => (await api.post("/custody/saveCompany", { table: "party", key: String(key), values })).data;

// Vessels (Tanımlamalar > Gemiler) and voyages (Sevkiyat İşlemleri > İhracat (Gemi) Planlama)
export const getVessels = async () => (await api.post("/custody/vessels", {})).data;
export const saveVessel = async (vessel) => (await api.post("/custody/saveVessel", vessel)).data;
export const getVoyages = async (onlyOpen) => (await api.post("/custody/voyages", { only_open: onlyOpen })).data;
export const getVoyage = async (id) => (await api.post("/custody/voyage", { id })).data;
export const saveVoyage = async (voyage) => (await api.post("/custody/saveVoyage", voyage)).data;
export const setVoyageMilestone = async (id, milestone, at) => (await api.post("/custody/voyageMilestone", { id, milestone, at })).data;
// Gemi Yükleme terminal screen: scan a VIN onto an open voyage's loading list (same backend as the web panel's "Yükleme listesi").
export const voyageLoadTerminal = async (voyageId, chassisNo, warehouseId) =>
    (await api.post("/custody/voyageLoadTerminal", { voyage_id: voyageId, chassis_no: chassisNo, warehouse_id: warehouseId })).data;
// action PLAN / REMOVE / LOAD / DISCHARGE; either chassis_nos (pasted) or file (.xlsx / .csv)
export const voyageCars = async (data, file) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file, file.name);
    return (await api.post("/custody/voyageCars", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

// Train services (Tanımlamalar > Trenler) and journeys (Sevkiyat İşlemleri > Tren Seferleri)
export const getTrainServices = async () => (await api.post("/custody/trainServices", {})).data;
export const saveTrainService = async (trainService) => (await api.post("/custody/saveTrainService", trainService)).data;
export const getTrainRoutes = async (operatorPartyID) => (await api.post("/custody/trainRoutes", { operator_party_id: operatorPartyID })).data;
export const saveTrainRoute = async (route) => (await api.post("/custody/saveTrainRoute", route)).data;
export const getTrainJourneys = async (onlyOpen) => (await api.post("/custody/trainJourneys", { only_open: onlyOpen })).data;
export const getTrainJourney = async (id) => (await api.post("/custody/trainJourney", { id })).data;
export const saveTrainJourney = async (journey) => (await api.post("/custody/saveTrainJourney", journey)).data;
export const setTrainJourneyMilestone = async (id, milestone, at) => (await api.post("/custody/trainJourneyMilestone", { id, milestone, at })).data;
// Tren Yükleme / Tren Boşaltma terminal screens: scan a VIN onto/off an open train journey (same backend shape as voyageLoadTerminal).
export const trainLoadTerminal = async (journeyId, chassisNo, warehouseId) =>
    (await api.post("/custody/trainLoadTerminal", { journey_id: journeyId, chassis_no: chassisNo, warehouse_id: warehouseId })).data;
export const trainUnloadTerminal = async (journeyId, chassisNo, warehouseId) =>
    (await api.post("/custody/trainUnloadTerminal", { journey_id: journeyId, chassis_no: chassisNo, warehouse_id: warehouseId })).data;
// action PLAN / REMOVE / LOAD / UNLOAD; either chassis_nos (pasted) or file (.xlsx / .csv)
export const trainJourneyCars = async (data, file) => {
    const form = new FormData();
    form.append("data", JSON.stringify(data));
    if (file) form.append("file", file, file.name);
    return (await api.post("/custody/trainJourneyCars", form, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
// Undo a single-chassis load (stoğa geri dön): reverts the car's WMS zone and drops the voyage/journey's loaded count.
export const undoVoyageLoad = async (voyageId, chassisNo) =>
    (await api.post("/custody/undoVoyageLoad", { voyage_id: voyageId, chassis_no: chassisNo })).data;
export const undoTrainJourneyLoad = async (journeyId, chassisNo) =>
    (await api.post("/custody/undoTrainJourneyLoad", { journey_id: journeyId, chassis_no: chassisNo })).data;

// Per-company data scope (row 210; Tanımlamalar > Firmalar > Veri kapsamı)
export const getPartyScopes = async () => (await api.post("/custody/partyScopes", {})).data;
export const savePartyScope = async (scope) => (await api.post("/custody/savePartyScope", scope)).data;
